import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, Loader2, MapPin, Phone, Mail, Upload, Image as ImageIcon, X, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { businessPageAccess } from '../../lib/roles';
import { businessApi, errorMessage } from '../../lib/api';
import { resolveAssetUrl, flag } from '../../config';
import BusinessHeader from '../../components/business/BusinessHeader';
import ConfirmDialog from '../../components/business/ConfirmDialog';
// Onboarding only offers venues within this radius; Settings applies the same rule so a
// business cannot add here what onboarding would have refused (audit row 68).
import { HUB_RADIUS_MILES } from '../../components/business/hubRules';

const BusinessSettings = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [business, setBusiness] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    business_location: '',
    address: '',
    phone: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: ''
  });

  // Check if user has business access (role logic: lib/roles.js businessPageAccess)
  const hasBusinessAccess = businessPageAccess(user);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !hasBusinessAccess)) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, user, navigate, hasBusinessAccess]);

  const [userBusinesses, setUserBusinesses] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [initialHubs, setInitialHubs] = useState([]);
  const [hubDistances, setHubDistances] = useState(null);
  const [confirmRemoveImage, setConfirmRemoveImage] = useState(false);
  const [removingImage, setRemovingImage] = useState(false);
  const hubCap = typeof subscription?.location_limit === 'number' ? subscription.location_limit : null;

  // Same distance rule the onboarding wizard applies, loaded in the background so the
  // venue list renders immediately (audit row 68). Venues already selected stay
  // selectable even if they now fall outside the radius.
  const distancesLoaded = useRef(false);
  const loadHubDistances = async (businessData) => {
    if (distancesLoaded.current) return;
    const parts = [businessData?.address, businessData?.city, businessData?.state, businessData?.zip_code]
      .filter(Boolean).join(', ');
    const fullAddress = parts || businessData?.business_location;
    if (!fullAddress) return;
    try {
      const res = await businessApi.post('/business/calculate-hub-distances', { address: fullAddress });
      if (res.data?.geocoding_failed) return;
      const byId = {};
      (res.data?.hubs || []).forEach((h) => { byId[h.hub_id] = h; });
      distancesLoaded.current = true;
      setHubDistances(byId);
    } catch (e) { /* distances are advisory; the list still works without them */ }
  };

  const fetchUserBusinesses = async () => {
    if (!flag('MULTI_BUSINESS')) return;
    try {
      const response = await businessApi.get('/business/my-businesses');
      setUserBusinesses(response.data.businesses || []);
    } catch (error) {
      console.error('Error fetching user businesses:', error);
    }
  };

  const handleSwitchBusiness = async (businessId) => {
    try {
      await businessApi.post(`/business/switch/${businessId}`, {});
      window.location.reload();
    } catch (error) {
      console.error('Error switching business:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated && hasBusinessAccess) {
      fetchBusinessData();
      fetchUserBusinesses();
    }
  }, [isAuthenticated, user, hasBusinessAccess]);

  const fetchBusinessData = async () => {
    try {
      const subRes = await businessApi.get('/business/subscription');
      setSubscription(subRes.data);
    } catch (e) { /* cap is a convenience; the server still enforces it */ }
    try {
      const businessRes = await businessApi.get('/business/profile');
      const locationsRes = await businessApi.get('/locations');

      const businessData = businessRes.data;
      setBusiness(businessData);
      setLocations(locationsRes.data);
      setSelectedHubs(businessData.hub_ids || []);
      setInitialHubs(businessData.hub_ids || []);
      setImagePreview(businessData.image_url || null);
      loadHubDistances(businessData);
      
      setFormData({
        name: businessData.name || '',
        description: businessData.description || '',
        business_location: businessData.business_location || '',
        address: businessData.address || '',
        phone: businessData.phone || '',
        website: businessData.website || '',
        contact_first_name: businessData.contact_first_name || user?.first_name || '',
        contact_last_name: businessData.contact_last_name || user?.last_name || '',
        contact_email: businessData.contact_email || user?.email || ''
      });
    } catch (error) {
      console.error('Error fetching business data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (selectedHubs.length === 0) {
        setMessage({ type: 'error', text: 'Pick at least one venue where your deal should appear.' });
        setSaving(false);
        return;
      }
      if (hubCap && selectedHubs.length > hubCap) {
        setMessage({ type: 'error', text: `Your plan covers ${hubCap} venue${hubCap === 1 ? '' : 's'}. Deselect one first.` });
        setSaving(false);
        return;
      }
      const hubsChanged = JSON.stringify([...selectedHubs].sort()) !== JSON.stringify([...initialHubs].sort());
      // Venues first (it is the call that can be refused), then the profile — never a half-applied save.
      if (hubsChanged) {
        await businessApi.post('/business/select-hubs', { hub_ids: selectedHubs });
      }
      await businessApi.put('/business/profile', formData);
      
      setMessage({ type: 'success', text: 'Business information updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      fetchBusinessData();
    } catch (error) {
      console.error('Error updating business:', error);
      setMessage({ type: 'error', text: errorMessage(error, 'Failed to update business information') });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setSaving(false);
    }
  };

  // A venue outside the radius can be kept (an admin may have assigned it) but not added.
  const hubOutOfRange = (hubId) => !!hubDistances && hubDistances[hubId]
    && hubDistances[hubId].within_range === false && !initialHubs.includes(hubId);

  const toggleHub = (hubId) => {
    if (hubOutOfRange(hubId) && !selectedHubs.includes(hubId)) {
      setMessage({ type: 'error', text: `Venues must be within ${HUB_RADIUS_MILES} miles of your business address.` });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
      return;
    }
    if (selectedHubs.includes(hubId)) {
      setSelectedHubs(selectedHubs.filter(id => id !== hubId));
    } else if (hubCap === 1) {
      setSelectedHubs([hubId]); // single-venue plan: replace
    } else if (hubCap && selectedHubs.length >= hubCap) {
      setMessage({ type: 'error', text: `Your plan covers ${hubCap} venues. Deselect one first.` });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    } else {
      setSelectedHubs([...selectedHubs, hubId]);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      setMessage({ type: 'error', text: 'Please upload a JPG, PNG, or WebP image' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image too large. Maximum size is 8MB' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });
    try {
      const body = new FormData();
      body.append('file', file);

      // multipart: let the browser set the boundary, so no explicit Content-Type here
      const response = await businessApi.post('/business/upload-banner', body);

      setImagePreview(response.data.image_url);
      setMessage({ type: 'success', text: 'Image uploaded successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: errorMessage(error, 'Failed to upload image') });
      setTimeout(() => setMessage({ type: '', text: '' }), 8000);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    setRemovingImage(true);
    try {
      await businessApi.put('/business/profile', { image_url: null });
      setConfirmRemoveImage(false);
      setImagePreview(null);
      setMessage({ type: 'success', text: 'Image removed successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error removing image:', error);
      setMessage({ type: 'error', text: errorMessage(error, 'Failed to remove image') });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setRemovingImage(false);
    }
  };

  // V5 Input Focus Handler
  const handleInputFocus = (e) => {
    e.target.style.borderColor = '#8b5cf6';
    e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
    e.target.style.boxShadow = 'none';
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <Loader2 className="animate-spin" size={48} style={{ color: '#8b5cf6' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
      {/* V5 Business Header */}
      <BusinessHeader 
        user={user}
        userName={`${user?.first_name || ''} ${user?.last_name || ''}`}
        currentBusiness={business}
        businesses={userBusinesses}
        onSwitchBusiness={handleSwitchBusiness}
      />

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
        {/* Page Title - V5 Design Language */}
        <div className="mb-8">
          <p className="font-mono text-xs mb-2" style={{ color: '#8b5cf6' }}>// edit_business</p>
          <h1 className="text-[32px] text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
            Business Details
          </h1>
          <p className="mt-2" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
            Update your business information and contact details
          </p>
        </div>

        {/* Main Card - V5 Design Language */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-5 md:p-8"
          style={{ 
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #8b5cf6',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
          }}
        >
          {/* Success/Error Message - V5 Colors */}
          <AnimatePresence>
            {message.text && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-6 p-4 rounded-xl flex items-center gap-3"
                style={message.type === 'success' 
                  ? { backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.2)', color: '#22c55e' }
                  : { backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#ef4444' }
                }
              >
                {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                <p className="text-[14px] font-medium">{message.text}</p>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSave} className="space-y-6">
            {/* Business Image Upload */}
            <div>
              <label className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Business Image
              </label>
              <p className="text-[13px] mb-3" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                Recommended: 16:9 aspect ratio (1920x1080). Max 8MB. This appears on deal cards and your business page.
              </p>
              
              {imagePreview ? (
                <div className="relative group">
                  <img
                    src={resolveAssetUrl(imagePreview)}
                    alt="Business"
                    className="w-full h-48 object-cover rounded-xl"
                    style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 text-white font-semibold rounded-xl flex items-center gap-2 transition-all hover:-translate-y-0.5"
                      style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)' }}
                    >
                      <Upload size={16} /> Change
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmRemoveImage(true)}
                      className="px-4 py-2 bg-white font-semibold rounded-xl flex items-center gap-2 transition-all hover:-translate-y-0.5"
                      style={{ border: '2px solid rgba(239, 68, 68, 0.3)', color: '#ef4444' }}
                    >
                      <X size={16} /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-48 rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all"
                  style={{ 
                    border: uploading ? '2px dashed #8b5cf6' : '2px dashed rgba(30, 27, 75, 0.2)',
                    backgroundColor: uploading ? 'rgba(139, 92, 246, 0.05)' : 'transparent'
                  }}
                  onMouseEnter={(e) => { if (!uploading) { e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.05)'; }}}
                  onMouseLeave={(e) => { if (!uploading) { e.currentTarget.style.borderColor = 'rgba(30, 27, 75, 0.2)'; e.currentTarget.style.backgroundColor = 'transparent'; }}}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="animate-spin mb-2" size={32} style={{ color: '#8b5cf6' }} />
                      <p className="text-[14px]" style={{ color: '#8b5cf6' }}>Uploading...</p>
                    </>
                  ) : (
                    <>
                      <ImageIcon size={32} className="mb-2" style={{ color: 'rgba(30, 27, 75, 0.4)' }} />
                      <p className="text-[14px] font-semibold text-[#1e1b4b]">Click to upload business image</p>
                      <p className="text-[13px] mt-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>JPG, PNG, or WebP • Max 1920x1080 • 8MB limit</p>
                    </>
                  )}
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleImageUpload} className="hidden" />
            </div>

            {/* Business Name */}
            <div>
              <label htmlFor="biz-name" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Business Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                id="biz-name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                required
                className="w-full h-[48px] px-4 text-[16px] rounded-xl transition-all outline-none"
                style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                placeholder="e.g., Joe's Coffee Shop"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="biz-description" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Business Description
              </label>
              <textarea
                id="biz-description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                rows={4}
                className="w-full px-4 py-3 text-[16px] rounded-xl transition-all outline-none resize-none"
                style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                placeholder="Tell customers about your business..."
              />
            </div>

            {/* Location */}
            <div>
              <label htmlFor="biz-location" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Business Location <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div className="relative">
                <MapPin size={20} className="absolute left-4 top-3.5" style={{ color: 'rgba(30, 27, 75, 0.4)' }} />
                <input
                  type="text"
                  id="biz-location"
                  name="business_location"
                  value={formData.business_location}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  required
                  className="w-full h-[48px] pl-12 pr-4 text-[16px] rounded-xl transition-all outline-none"
                  style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                  placeholder="e.g., Medford, Portland, Boston"
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <label htmlFor="biz-address" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Street Address
              </label>
              <input
                type="text"
                id="biz-address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                className="w-full h-[48px] px-4 text-[16px] rounded-xl transition-all outline-none"
                style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                placeholder="e.g., 123 Main Street"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="biz-phone" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Business Phone
              </label>
              <div className="relative">
                <Phone size={20} className="absolute left-4 top-3.5" style={{ color: 'rgba(30, 27, 75, 0.4)' }} />
                <input
                  type="tel"
                  id="biz-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  className="w-full h-[48px] pl-12 pr-4 text-[16px] rounded-xl transition-all outline-none"
                  style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                  placeholder="e.g., (555) 123-4567"
                />
              </div>
            </div>

            {/* Website */}
            <div>
              <label htmlFor="biz-website" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Business Website
              </label>
              <input
                type="url"
                id="biz-website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                className="w-full h-[48px] px-4 text-[16px] rounded-xl transition-all outline-none"
                style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                placeholder="e.g., https://www.mybusiness.com"
              />
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid rgba(30, 27, 75, 0.05)', margin: '32px 0' }}></div>

            {/* Contact Person Section */}
            <h3 className="text-[24px] text-[#1e1b4b] mb-4" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
              Contact Person
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contact-first-name" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                  First Name
                </label>
                <input
                  type="text"
                  id="contact-first-name"
                  name="contact_first_name"
                  value={formData.contact_first_name}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  className="w-full h-[48px] px-4 text-[16px] rounded-xl transition-all outline-none"
                  style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                  placeholder="First name"
                />
              </div>
              <div>
                <label htmlFor="contact-last-name" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  id="contact-last-name"
                  name="contact_last_name"
                  value={formData.contact_last_name}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  className="w-full h-[48px] px-4 text-[16px] rounded-xl transition-all outline-none"
                  style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                  placeholder="Last name"
                />
              </div>
            </div>

            {/* Contact Email */}
            <div>
              <label htmlFor="contact-email" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Contact Email
              </label>
              <div className="relative">
                <Mail size={20} className="absolute left-4 top-3.5" style={{ color: 'rgba(30, 27, 75, 0.4)' }} />
                <input
                  type="email"
                  id="contact-email"
                  name="contact_email"
                  value={formData.contact_email}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  className="w-full h-[48px] pl-12 pr-4 text-[16px] rounded-xl transition-all outline-none"
                  style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                  placeholder="contact@business.com"
                />
              </div>
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid rgba(30, 27, 75, 0.05)', margin: '32px 0' }}></div>

            {/* Hub Locations Section - Using Teal for Locations per V5 */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}>
                <MapPin size={20} className="text-[#14b8a6]" />
              </div>
              <div>
                <h3 className="text-[24px] text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
                  Partner venues{hubCap ? ` (${selectedHubs.length} of ${hubCap} selected)` : ''}
                </h3>
                <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                  {hubCap ? `Your plan covers ${hubCap} venue${hubCap === 1 ? '' : 's'}. ` : ''}Choose where your deal appears, within {HUB_RADIUS_MILES} miles of your business.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {locations.map((location) => {
                const distance = hubDistances?.[location.id]?.distance_miles;
                const outOfRange = hubOutOfRange(location.id);
                return (
                <label
                  key={location.id}
                  className="flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200"
                  style={{
                    border: selectedHubs.includes(location.id) ? '2px solid #14b8a6' : '2px solid rgba(30, 27, 75, 0.1)',
                    backgroundColor: selectedHubs.includes(location.id) ? 'rgba(20, 184, 166, 0.05)' : 'transparent',
                    borderLeft: selectedHubs.includes(location.id) ? '4px solid #14b8a6' : '2px solid rgba(30, 27, 75, 0.1)',
                    opacity: outOfRange ? 0.6 : 1,
                    cursor: outOfRange ? 'not-allowed' : 'pointer'
                  }}
                >
                  <div 
                    className="w-5 h-5 rounded flex items-center justify-center transition-all"
                    style={{ 
                      backgroundColor: selectedHubs.includes(location.id) ? '#14b8a6' : 'white',
                      border: selectedHubs.includes(location.id) ? 'none' : '2px solid rgba(30, 27, 75, 0.2)'
                    }}
                  >
                    {selectedHubs.includes(location.id) && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedHubs.includes(location.id)}
                    onChange={() => toggleHub(location.id)}
                    className="hidden"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-[#1e1b4b]">{location.name}</p>
                    <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>{location.address}</p>
                    {outOfRange && (
                      <p className="text-[12px] mt-1" style={{ color: '#ef4444' }}>
                        More than {HUB_RADIUS_MILES} miles from your business address
                      </p>
                    )}
                  </div>
                  {distance != null && (
                    <span className="text-[12px] font-semibold px-2 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)', color: 'rgba(30, 27, 75, 0.6)' }}>
                      {distance} mi
                    </span>
                  )}
                  <MapPin size={20} style={{ color: selectedHubs.includes(location.id) ? '#14b8a6' : 'rgba(30, 27, 75, 0.3)' }} />
                </label>
                );
              })}
            </div>

            {/* Action Buttons - V5 Design Language */}
            <div className="flex justify-end gap-4 pt-8">
              {/* Ghost button for Cancel */}
              <button
                type="button"
                onClick={() => navigate('/business/dashboard')}
                className="px-6 py-3 rounded-xl font-semibold transition-all duration-200"
                style={{ color: 'rgba(30, 27, 75, 0.7)' }}
              >
                Cancel
              </button>
              {/* Purple primary button for Save */}
              <button
                type="submit"
                disabled={saving}
                className="px-8 py-3 text-white rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 flex items-center gap-2"
                style={{ 
                  background: saving ? 'rgba(30, 27, 75, 0.3)' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                  boxShadow: saving ? 'none' : '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)'
                }}
              >
                {saving ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      <ConfirmDialog
        open={confirmRemoveImage}
        title="Remove business image?"
        message="Your deal cards will show without a banner until you upload a new one."
        confirmLabel="Remove image"
        busy={removingImage}
        onConfirm={handleRemoveImage}
        onCancel={() => setConfirmRemoveImage(false)}
      />
    </div>
  );
};

export default BusinessSettings;
